import { useState, useEffect, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { FESTIVAL_ID_RE } from "@/lib/festival-utils";
import { SAFE_ID } from "@/lib/id-utils";
import { parseLocalDate } from "@/lib/date-utils";
import { trackEvent } from "@/lib/analytics";
import type { TravelerProfile } from "@shared/schema";
import { ADULT_REQUIRED_EVENT } from "@shared/auth-codes";
import type { ProfileStep, ProfileCompletionDialogProps } from "./types";

/** Post-onboarding destination — dashboard for minimal friction */
export const POST_ONBOARDING_DESTINATION = "/dashboard";

/** Fields saved during onboarding — only essentials to minimize drop-offs */
export const ONBOARDING_SAVE_FIELDS = ["preferredName", "gender", "dateOfBirth"] as const;

export function useProfileCompletionData({ externalOpen, onClose }: ProfileCompletionDialogProps) {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [, navigate] = useLocation();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<ProfileStep>("profile");
  const [preferredName, setPreferredName] = useState("");
  const [gender, setGender] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [completedFullOnboarding, setCompletedFullOnboarding] = useState(false);

  const isExternalMode = externalOpen !== undefined;

  // Initialize form from user data
  useEffect(() => {
    if (isExternalMode) {
      if (externalOpen && user) {
        setPreferredName(user.preferredName || user.firstName || "");
        setGender(user.gender || "");
        const dob = user.dateOfBirth ? parseLocalDate(user.dateOfBirth) : null;
        setDateOfBirth(dob && !isNaN(dob.getTime()) ? dob.toLocaleDateString("sv-SE") : "");
        setStep("profile");
        setCompletedFullOnboarding(false);
      }
      setOpen(!!externalOpen);
      return;
    }
    if (isAuthenticated && user) {
      if (!user.preferredName) {
        setPreferredName(user.firstName || "");
        const dismissed = sessionStorage.getItem("profile-completion-dismissed");
        if (!dismissed) {
          const timer = setTimeout(() => setOpen(true), 1500);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [isAuthenticated, user, externalOpen, isExternalMode]);

  // Re-open the dialog when the server signals ADULT_REQUIRED (null/under-18 DOB
  // hitting an adult-gated endpoint). queryClient clears the dismissal flag and
  // fires this event; we just open the dialog and prefill from current user.
  //
  // ACCEPTED UX (not a bug): the original mutation that triggered the 403 is
  // NOT replayed after DOB verification — the user must re-click the action
  // (e.g., "Create trip") once they've completed onboarding. Reasoning:
  //   1. Replay would require buffering arbitrary mutation payloads in a ref,
  //      which complicates state management for an edge case (legacy null-DOB
  //      adults are vanishingly rare post-signup-gate).
  //   2. Re-clicking is a clear UX signal that the action was deferred until
  //      verification, not silently completed in the background.
  //   3. If user-testing surfaces friction, add a mutation-replay queue here.
  useEffect(() => {
    if (isExternalMode) return;
    const handler = () => {
      if (user) {
        setPreferredName(user.preferredName || user.firstName || "");
        const dob = user.dateOfBirth ? parseLocalDate(user.dateOfBirth) : null;
        setDateOfBirth(dob && !isNaN(dob.getTime()) ? dob.toLocaleDateString("sv-SE") : "");
        setStep("profile");
      }
      setOpen(true);
    };
    window.addEventListener(ADULT_REQUIRED_EVENT, handler);
    return () => window.removeEventListener(ADULT_REQUIRED_EVENT, handler);
  }, [user, isExternalMode]);

  // Handle post-onboarding navigation
  useEffect(() => {
    if (completedFullOnboarding) {
      if (isExternalMode) {
        setOpen(false);
        onClose?.();
      } else {
        sessionStorage.setItem("profile-completion-dismissed", "true");
        sessionStorage.setItem("onboarding-active", "true");
        setOpen(false);
        const festivalJoin = sessionStorage.getItem("troopn-festival-join");
        const pendingTripJoin = sessionStorage.getItem("pendingTripJoin");
        if (festivalJoin && FESTIVAL_ID_RE.test(festivalJoin)) {
          sessionStorage.removeItem("troopn-festival-join");
          navigate(`/festival-guide?festival=${festivalJoin}`);
        } else if (pendingTripJoin && SAFE_ID.test(pendingTripJoin)) {
          // Don't remove pendingTripJoin here — TripDetail reads and clears it to auto-open the join dialog
          navigate(`/trip/${pendingTripJoin}`);
        } else {
          navigate(POST_ONBOARDING_DESTINATION);
        }
      }
    }
  }, [completedFullOnboarding, navigate, isExternalMode, onClose]);

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async (data: { preferredName: string; gender?: string; dateOfBirth?: string }) => {
      const payload: Record<string, unknown> = { preferredName: data.preferredName };
      if (data.gender) payload.gender = data.gender;
      if (data.dateOfBirth) payload.dateOfBirth = data.dateOfBirth;
      const res = await apiRequest("PATCH", "/api/auth/user", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: t("profileCompletion.profileUpdated"), description: t("profileCompletion.profileUpdatedDesc") });
      trackEvent("onboarding_step_completed", { step: "profile" });
      setStep("archetype");
    },
    onError: (err: unknown) => {
      // The profile PATCH rejects with actionable 400s (under-18 DOB, DOB
      // locked after verification). apiRequest throws `${status}: ${message}`
      // — showing the generic copy instead left the user stuck with no idea
      // what to change.
      const raw = err instanceof Error ? err.message : "";
      const serverMessage = raw.replace(/^\d{3}:\s*/, "").trim();
      toast({
        title: t("profileCompletion.error"),
        description: serverMessage || t("profileCompletion.errorDesc"),
        variant: "destructive",
      });
    },
  });

  const archetypeMutation = useMutation({
    mutationFn: async (profile: TravelerProfile) => {
      const res = await apiRequest("PATCH", "/api/auth/user", { travelerProfile: profile });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: t("profileCompletion.archetypeSaved"), description: t("profileCompletion.archetypeSavedDesc") });
      trackEvent("onboarding_step_completed", { step: "archetype" });
      setStep("guild");
    },
    onError: () => {
      toast({ title: t("profileCompletion.error"), description: t("profileCompletion.errorDesc"), variant: "destructive" });
    },
  });

  const guildMutation = useMutation({
    mutationFn: async (guildType: string) => {
      const existing = (user?.travelerProfile as Record<string, unknown>) || {};
      const res = await apiRequest("PATCH", "/api/auth/user", {
        travelerProfile: { ...existing, guildType },
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      trackEvent("onboarding_step_completed", { step: "guild" });
      setStep("valueReveal");
    },
    onError: () => {
      // Non-critical — skip to next step on error
      setStep("valueReveal");
    },
  });

  const handleGuildSkip = useCallback(() => {
    setStep("valueReveal");
  }, []);

  const handleValueRevealDone = useCallback(() => {
    trackEvent("onboarding_step_completed", { step: "value_reveal" });
    setCompletedFullOnboarding(true);
  }, []);

  // Handlers
  const handleClose = useCallback(() => {
    if (isExternalMode) {
      setOpen(false);
      onClose?.();
    } else {
      sessionStorage.setItem("profile-completion-dismissed", "true");
      setOpen(false);
    }
  }, [isExternalMode, onClose]);

  const handleSave = useCallback(() => {
    saveMutation.mutate({
      preferredName,
      gender: gender || undefined,
      dateOfBirth: dateOfBirth || undefined,
    });
  }, [preferredName, gender, dateOfBirth, saveMutation]);

  return {
    // Auth
    user,
    isAuthenticated,

    // Dialog state
    open,
    setOpen,
    step,
    isExternalMode,

    // Form state
    preferredName,
    setPreferredName,
    gender,
    setGender,
    dateOfBirth,
    setDateOfBirth,

    // Mutations
    saveMutation,
    archetypeMutation,
    guildMutation,

    // Handlers
    handleClose,
    handleGuildSkip,
    handleSave,
    handleValueRevealDone,
  };
}
